import { type RefObject, useEffect } from "react";

export interface UseIntersectionObserverOptions {
  /** Invoked whenever `targetRef` begins intersecting the root (or viewport). */
  onIntersect: () => void;
  /**
   * When `false`, no observer is attached and `onIntersect` never fires.
   * Useful for gating infinite-scroll triggers (e.g. while a page is in flight
   * or once there are no more pages). Defaults to `true`.
   */
  enabled?: boolean;
  /**
   * Scrollable ancestor used as the observer's root. When omitted (or its
   * `current` is `null`), intersection is measured against the browser viewport.
   */
  root?: RefObject<Element | null>;
  /**
   * Margin grown around the root before computing intersection, e.g. `"200px"`
   * to fire before the target is fully on screen. Same syntax as CSS margins.
   */
  rootMargin?: string;
  /** Visibility ratio(s) at which the callback fires. Defaults to `0`. */
  threshold?: number | number[];
}

/**
 * Fires a callback when a target element scrolls into view, via the native
 * `IntersectionObserver`. The observer is created when the target (and, if
 * provided, the root) is mounted and `enabled` is `true`, and is torn down on
 * cleanup or when any option changes.
 *
 * The classic use is infinite scroll: render a zero-height sentinel at the end
 * of a list, point `targetRef` at it, pass the scroll container as `root`, and
 * call your "load more" function from `onIntersect` — gated by `enabled` so it
 * stops at the last page and never fires while a fetch is already running.
 *
 * Pass a stable `onIntersect` (e.g. a TanStack `fetchNextPage` or a
 * `useCallback`); an inline arrow re-creates the observer on every render.
 *
 * @example
 * ```tsx
 * const sentinelRef = useRef<HTMLDivElement>(null);
 * useIntersectionObserver(sentinelRef, {
 *   root: scrollContainerRef,
 *   rootMargin: "200px",
 *   enabled: hasNextPage && !isFetchingNextPage,
 *   onIntersect: fetchNextPage,
 * });
 * // ...
 * <div ref={sentinelRef} />
 * ```
 */
export const useIntersectionObserver = (
  targetRef: RefObject<Element | null>,
  { onIntersect, enabled = true, root, rootMargin, threshold }: UseIntersectionObserverOptions
) => {
  useEffect(() => {
    const target = targetRef.current;
    if (!enabled || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onIntersect();
      },
      { root: root?.current ?? null, rootMargin, threshold }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetRef, onIntersect, enabled, root, rootMargin, threshold]);
};
